/* @flow weak */
import {Observable} from 'rxjs';
import {mapTo} from 'rxjs/operators';
import {combineEpics} from 'redux-observable';
import {replace} from 'react-router-redux';
import {Map, List, fromJS} from 'immutable';
import {filter, prop, path} from 'ramda';
import {saveTextToFile, isObject} from 'trustar/utils';
import moment from 'moment';
import {parseJsonIfPossible} from 'trustar/reports/extraction';
import createDebugger from 'debug';

const debug = createDebugger('app:modules:reports:epics'); // eslint-disable-line no-unused-vars

import {LOCATION_CHANGE} from 'modules/router/action-types';
import {
  REMOVE,
  REMOVE_SUCCESS,
  REMOVE_FAILURE,
  REMOVE_ATTRIBUTE,
  REMOVE_ATTRIBUTE_SUCCESS,
  REMOVE_ATTRIBUTE_FAILURE,
  SET_ENCLAVE_TAG,
  REMOVE_ENCLAVE_TAG,
  REMOVE_ENCLAVE_TAG_SUCCESS,
  REMOVE_ENCLAVE_TAG_FAILURE,
  REINDEX,
  REINDEX_SUCCESS,
  REINDEX_FAILURE,
  STORE,
  STORE_SUCCESS,
  STORE_FAILURE,
  FETCH_LIST,
  FETCH_LIST_SUCCESS,
  FETCH_DATA_ALREADY_CACHED,
  SET_DISPLAY_OPTIONS,
  SET_ENCLAVE_TAGS_BULK,
  REFETCH_REPORTS,
  FETCH_METADATA,
  SET_ENCLAVE_TAG_SUCCESS,
  SET_ENCLAVE_TAG_FAILURE,
  SET_ATTRIBUTE,
  SET_ATTRIBUTE_SUCCESS,
  SET_ATTRIBUTE_FAILURE,
  EXPORT_REPORT,
  COPY_REPORT,
  COPY_REPORT_SUCCESS,
  COPY_REPORT_FAILURE,
  MOVE_REPORT,
  MOVE_REPORT_SUCCESS,
  MOVE_REPORT_FAILURE,
} from './action-types.js';
import {
  FETCH_ENCLAVE_TAGS
} from 'modules/tags/action-types';
import * as Actions from './actions';
import {
  SUBMIT_REPORT_SUCCESS,
  UPDATE_REPORT_SUCCESS,
  POLL_FOR_REPORT,
  REPORT_POLLING_FAILURE,
  REPORT_PROCESSED,
} from 'modules/create/action-types.js';
import * as Constellation from 'modules/constellation';
import * as Search from 'modules/search';
import * as Indicators from 'modules/indicators';
import {FETCH_REPORTS_SUCCESS, SEARCH_REPORTS_SUCCESS} from 'modules/search/action-types';
import {LOAD_REPORT_DATA} from 'modules/create/action-types';
import {FETCH_SUCCESS as FETCH_PROFILE_SUCCESS} from 'modules/profile/action-types.js';
import {enclave as enclaveById, enclavesMap} from 'modules/enclave/getters.js';
import {createEnclave, type Enclave} from 'modules/enclave/types.js';
import {FETCH_SUCCESS as FETCH_ENCLAVE_SUCCESS} from 'modules/enclave/action-types.js';
import {REPORTS_ENDPOINT, PAGE_SIZE, MAX_TAG_LENGTH, MAX_RETRY_NEW_REPORT_POLLING} from './constants.js';
import {displayOptions, metadataCache, currentReportOrListItem} from './getters.js';
import {recordFetchById as fetchConstellationById} from 'modules/constellation/epics.js';
import * as profileGetters from 'modules/profile/getters.js';
import {warn, error, success} from 'modules/alerts/actions';
import {reportIdsFromList, metadataUrl} from './utils';
import {formatTagsToBulk, formatBulkTagResponse, bulkTagSuccessMessage} from 'modules/tags/utils';
import {toJSON} from 'modules/create/utils';
import { ADD_ALERT } from 'modules/alerts/action-types';

import React from "react";

/* Log possible errors and keep Rx stream alive */
const handleError = (error, stream) => {
  const message = 'Error in Reports Epics:';
  // TODO_LOG Raven.captureException(new Error(message), {extra: { error }});
  console.warn(message);
  console.error(error);
  return stream;
};

/* Filter out the enclaves on a report that the user does not belong to. */
const mapEnclaves = (reportEnclaves?: List<any>, state): List<Enclave> => {
  if (!reportEnclaves || !(typeof reportEnclaves.map === 'function')) {
    return List();
  }

  const canViewAllEnclaves = profileGetters.hasPermission('get_data')(state);
  const enclaves: Map<any, any> = profileGetters.enclaves(state);
  return canViewAllEnclaves
    ? fromJS(reportEnclaves).map(createEnclave)
    : fromJS(enclaves)
        .toList()
        .map(createEnclave)
        .filter(x => {
          // $FlowIssue: This will NOT be void. See check at the top
          return reportEnclaves.map(y => y.get('id')).includes(x.get('id'));
        });
};

/**
 * Map the report object to something a bit more friendly from a UI perspective.
 *
 * TODO: It may be best to keep this sort of logic in the stores, the only issue
 * would be when we want to go back toJSON, it would probably have to be done in
 * the actions.
 */
const reportFromJson = (reportPointer: ReportListItemShape | ReportShape, state: Map<any, any>): Map<any, any> => {
  return fromJS(reportPointer)
    .mapEntries(([k, v]) => {
      switch (k) {
        case 'created':
        case 'updated':
          return [k, moment(v)];
        case 'enclaves':
          return ['enclaves', mapEnclaves(v, state)];
        default:
          return [k, v];
      }
    });
};

/**
 * Initialize the reports, using the user's enclaves
 *
 * NOTE: We were seeing postgres performance issues in prod and eliminating
 * community enclaves from the query is an effort to help address the proximate
 * problem.
 */
const initializeEpic = (action$, store) =>
  action$.ofType(FETCH_PROFILE_SUCCESS)
    .map(prop('payload'))
    .mergeMap(profile => {
      const enclaveFilters = profileGetters.groupedFilterEnclaves(store.getState())
        .get('INTERNAL', List())  // NOTE: Only show private enclaves for now
        .map(prop('id'))
        .toSet();

      const reportOptions = {
        enclaveFilters,
        includeCommunity: !enclaveFilters.size, // See NOTE
        search: '',
      };

      const SearchBeenInitializedAlready = Search.getters.searchPayloadHasBeenInitialized(store.getState());

      // Interdependency a-gogo :/
      let actionsToDispatch = [
        Actions.setDisplayOptions(reportOptions),
        Indicators.actions.setFetchOptions({enclaveFilters}),
        Indicators.actions.fetchIndicatorList(),
      ];

      if (!SearchBeenInitializedAlready) {
        actionsToDispatch = actionsToDispatch.concat([
          Search.actions.setSearchOptions({enclaveFilters}),
          Search.actions.updateReportFilters(Map({ enclaveFilters }), ['enclaveFilters']),
          Search.actions.updateEntityFilters(Map({ enclaveFilters }), ['enclaveFilters']),
        ]);
      }

      return actionsToDispatch;
    });

/*
 * Runs after a report is submitted or updatd. The report is persisted asynchronously
 * by the Ingest Service, so we pause the UI on the loading screen and poll the
 * server for the report status until it is updated as PROCESSED. Then we proceed
 * and fetch the report and constellation and redirect the user back into the app.
 *
 * NOTE: We takeUntil a specific navigation action, not REPLACE but PUSH, which
 * means the user initiated a navigation away from the current view. This is because
 * our replace actions also produce LOCATION_CHANGE actions which would otherwise
 * cancel the stream output.
 */
const pollReportAndStore = (action$, store, {ajax}) =>
  Observable.merge(
    action$.ofType(SUBMIT_REPORT_SUCCESS, UPDATE_REPORT_SUCCESS).delay(500),
    action$.ofType(POLL_FOR_REPORT)
  )
    .map(prop('payload'))
    .mergeMap(({id, submissionVersion, attempts = 0}) => {
      return ajax.get(`${REPORTS_ENDPOINT}/status/${id}?submissionVersion=${submissionVersion}`)
        .map(prop('response'))
        .mergeMap(status => {
          // If status is false recycle and poll again, otherwise complete stream:
          return status
            ? Observable.from([
              {type: REPORT_PROCESSED},
              success('Report processing complete'),
              replace(`/constellation/reports/${id}`)
            ]).delay(500)
            : Observable.of({type: POLL_FOR_REPORT, payload: {id, submissionVersion, attempts: attempts + 1}});
        })
        .catch(err => {
          if (attempts >= MAX_RETRY_NEW_REPORT_POLLING) {
            return Observable.from([
              {type: REPORT_POLLING_FAILURE},
              error('Something went wrong, try refreshing your browser.'),
              replace('/browse/reports')
            ]);
          }

          return Observable.of({
            type: POLL_FOR_REPORT,
            payload: {id, submissionVersion, attempts: attempts + 1}
          }).delay(1000);
        })
        .takeUntil( // See NOTE
          action$
            .ofType(LOCATION_CHANGE)
            .map(prop('payload'))
            .map(prop('action'))
            .filter(action => action === 'PUSH')
        )
        .catch(err => {
          return Observable.from([
            {type: REPORT_POLLING_FAILURE},
            error('Something went wrong, try refreshing your browser.'),
            replace('/browse/reports')
          ]);
        });
    });

const fetchAndStoreEpic = (action$, store, {ajax}) =>
  action$.ofType(STORE)
    .map(prop('payload'))
    .do((id) => !id && console.warn('No id passed when trying to fetch report'))
    .filter(Boolean)
    .mergeMap(id =>
      ajax.get(REPORTS_ENDPOINT + '/' + id)
        .map(prop('response'))
        .filter(isObject) // TODO: Is this check necessary? What might the server return?
        .map(x => reportFromJson(x, store.getState())) // TODO: This is store-level logic. The fact that it's still here is legacy
        .map(x => {
          // Convert user GUID to enclaves list to preserve all existing functionality
          const enclavesStore = enclavesMap(store.getState());
          const populatedEnclave = enclavesStore.get(x.get('enclaveGuid'));
          return x.get('enclaves') ? x : x.set('enclaves', List([populatedEnclave]));
        }) // TODO: This is store-level logic. The fact that it's still here is legacy
        .map(r => r.set('json', parseJsonIfPossible(r.get('reportBody'))))
        .mergeMap(report => [
          {
            type: STORE_SUCCESS,
            payload: report,
          },
          {
            type: FETCH_METADATA,
            payload: List([report.get('id')])
          }
        ])
        .catch(err => Observable.of({
          type: STORE_FAILURE,
          error: true,
          payload: err,
        })));

const enclaveTagErrorMessageObservable = (message: string) => {
  const err = new Error(message);
  return Observable.of({
    type: SET_ENCLAVE_TAG_FAILURE,
    error: true,
    payload: err,
    meta: {
      alert: {type: 'error', text: err.message},
    },
  });
}
// Enclave Tag Helpers
// TODO:FLOW
const formatTagObject = (reportId: string, tag: any, enclaveId: string) => {
  return {
    id: reportId,
    tag: tag.set('enclaveId', enclaveId)
  };
};


const enclaveTagEpic = (action$, store, {ajax}) =>
  action$.ofType(SET_ENCLAVE_TAG)
    .map(prop('payload'))
    .mergeMap(({id, name, enclaveId}) => {

      let message;
      if (name.length > MAX_TAG_LENGTH) message = `Tag name must be less than ${MAX_TAG_LENGTH} characters.`;
      if (!name.trim()) message = 'No tag name specified.';

      if (message) {
        const err = new Error(message);
        return Observable.of({
          type: SET_ENCLAVE_TAG_FAILURE,
          error: true,
          payload: err,
          meta: {
            alert: {type: 'error', text: err.message},
          },
        });
      }
    });

const setEnclaveTagsBulk = (action$, store, {ajax}) =>
  action$
    .ofType(SET_ENCLAVE_TAGS_BULK)
    .map(prop('payload'))
    .mergeMap(({id, addedTags, enclaveIds, removedTags, alertOnSuccess}) => {
      let message;

      const enclaveTagCombinations = enclaveIds.flatMap(enclaveId =>
        addedTags.map(name => {
          if (!name.trim()) message = 'No tag name specified.';
          if (name.length > MAX_TAG_LENGTH) message = `Tag name must be less than ${MAX_TAG_LENGTH} characters.`;
          return {id, enclaveId, name};
        })
      );

      const enclaveTagCombinationsForDeletion = removedTags && enclaveIds.flatMap(enclaveId =>
        removedTags.map(name => {
            if (!name.trim()) message = 'No tag name specified.';
            if (name.length > MAX_TAG_LENGTH) message = `Tag name must be less than ${MAX_TAG_LENGTH} characters.`;
            return {id, enclaveId, name};
          }
        ));

      if (message) {
        return enclaveTagErrorMessageObservable(message);
      }

      const formattedBulkTagPayload = formatTagsToBulk(enclaveTagCombinations, enclaveTagCombinationsForDeletion);

      return ajax.postJSON(`${REPORTS_ENDPOINT}/${id}/tags`, formattedBulkTagPayload)
        .map(prop('response'))
        .mergeMap(emptySuccessResponse => {
          /* Endpoint returns status:200 response null so we don't need its response payload, we just know we succeeded
          *  So we run the success actions on the request payload we sent.
          * */
          const allTagEnclavesMap = enclaveTagCombinations.concat(enclaveTagCombinationsForDeletion);
          const formattedBulkTagResponse = formatBulkTagResponse((formattedBulkTagPayload), allTagEnclavesMap);
          const {addedTags, removedTags} = formattedBulkTagResponse.toJS();

          const tagOperations = addedTags
            .map(tag => ({type: SET_ENCLAVE_TAG_SUCCESS, payload: {id, tag: Map(tag)}}))
            .concat(removedTags.map(tag => ({type: REMOVE_ENCLAVE_TAG_SUCCESS, payload: {id, tag: Map(tag)}})));

          const successMessage = bulkTagSuccessMessage(addedTags, removedTags);
          return alertOnSuccess && successMessage ? tagOperations.concat(success(successMessage)) : tagOperations;
        })
        .catch(err => Observable.of({
          type: SET_ENCLAVE_TAG_FAILURE,
          error: true,
          payload: err,
          meta: {
            alert: {type: 'error', text: 'Could not add enclave tag to report.'},
          },
        }));
    });

const attributeEpic = (action$, store, {ajax}) =>
  action$.ofType(SET_ATTRIBUTE)
    .map(prop('payload'))
    .mergeMap(({id, name}) => {
      if (name.length > 32) {
        const err = new Error('Attribute exceeded 32 characters.');
        return Observable.of({
          type: SET_ATTRIBUTE_FAILURE,
          error: true,
          payload: err,
          meta: {
            alert: {type: 'error', text: err.message},
          },
        });
      }

      return ajax.postJSON(`${REPORTS_ENDPOINT}/${id}/public-tags`, fromJS({name})) // TODO: Why use fromJS here?
        .map(prop('response'))
        .filter(isObject) // TODO: Is this check necessary? What might the server return?
        .map(x => reportFromJson(x, store.getState())) // TODO: This is store-level logic. The fact that it's still here is legacy
        .map(tag => ({
          type: SET_ATTRIBUTE_SUCCESS,
          payload: {id, tag},
        }))
        .catch(err => Observable.of({
          type: SET_ATTRIBUTE_FAILURE,
          error: true,
          payload: err,
          meta: {
            alert: {type: 'error', text: 'Could not add attribute to report.'},
          },
        }));
    });

/**
 * Fetch report metadata
 *
 * NOTE: Flatten and buffer. 5 is arbitrary. For now this value just gets us 5
 * batches (25/5). We need to mergeMap into a buffered observable in order to
 * get non-multiples of 5 numbers in the final buffer. The completion signal is
 * key here. If we buffered on the outer observable if we ever came back with
 * any number of reports that was not a multiple of 5 then the remainder would
 * simply never fire a full buffer off. They would be buffered but without a
 * complete signal never released since the buffer of 5 was never filled.
 *
 * NOTE: I added caching here so that we could simply dispatch a FETCH_METADATA
 * without worrying about whether or not it had already been fetched. However, if
 * `cacheDisabled` is true, the cache won't be used and the metadata will
 * be fetched from the server as usual.
 */
const fetchMetadataEpic = (action$, store, {ajax}) => {
  return Observable.merge(
    action$.ofType(FETCH_METADATA).map(({ payload, meta }) => ({ payload, meta })),
    Observable.merge(
      action$.ofType(FETCH_LIST_SUCCESS)
        .map(prop('payload'))
        .map(reportIdsFromList),
      action$.ofType(FETCH_REPORTS_SUCCESS)
        .map(prop('payload'))
        .map(d => d.get('data').keySeq().toList()),
      action$.ofType(SEARCH_REPORTS_SUCCESS)
        .map(prop('payload'))
        .map(d => d.get('items').map(item => item.get('guid'))),
    ).map((ids) => ({ payload: ids })),
  )
    .mergeMap(({ payload, meta = {} }) => {
      const getMetadata = (reportIds) => Observable.from(
        ajax.get(metadataUrl(reportIds)).map(prop('response')),
      );

      return Observable.if(
        () => meta.cacheDisabled,
        getMetadata(payload)
          .map((metadata) => Actions.fetchReportMetadataSuccess(metadata))
          .catch((error) => Actions.fetchReportMetadataFailure(error)),
        Observable.of(payload)
          .map(filter((id) => !metadataCache(store.getState()).get(id)))
          .mergeMap((ids) => Observable.if(
            () => ids.size === 0,
            mapTo({ type: FETCH_DATA_ALREADY_CACHED }),
            Observable.of(ids),
          ))
          .mergeMap((ids) => Observable.from(ids).bufferCount(10)) // See NOTE.
          .mergeMap((batch) => getMetadata(batch))
          .map((metadata) => Actions.fetchReportMetadataSuccess(metadata))
          .catch((error) => Actions.fetchReportMetadataFailure(error)),
      );
    })
    .catch((error) => Observable.of(Actions.fetchReportMetadataFailure(error)))
};

/* Sets display options for reports */
const setFetchOptionsEpic = (action$, store, {ajax}) =>
  action$.ofType(REFETCH_REPORTS, SET_DISPLAY_OPTIONS, REPORT_PROCESSED)
    .combineLatest(action$.ofType(FETCH_ENCLAVE_SUCCESS))
    .mergeMap(() => {
      const state = store.getState();
      const options = displayOptions(state);
      const enclaveFilters = fromJS(options.enclaveFilters)
        .toList()
        .map(id => enclaveById(id)(state));

      let searchDto = {
        startPage: options.page,
        pageSize: PAGE_SIZE,
        sortDirection: 'DESC',
        sortFields: ['updated'],
        enclaveFilters,
        tags: options.tags,
        excludedTags: options.excludedTags,
        includeCommunity: options.includeCommunity,
      };

      const validDistribution = options.includeCommunity || enclaveFilters.size > 0;

      if (!validDistribution) {
        return Observable.of(warn('Update selection filters to see results.'));
      } else {
        return Observable.of({type: FETCH_LIST, payload: searchDto});
      }
    });

const removeEnclaveTagEpic = (action$, store, {ajax}) =>
  action$.ofType(REMOVE_ENCLAVE_TAG)
    .mergeMap(({payload: {reportId, tagName}}) => {
      return ajax.delete(`${REPORTS_ENDPOINT}/${reportId}/enclave-tags/${tagName}`)
        .mergeMap(() => ([
          {type: REMOVE_ENCLAVE_TAG_SUCCESS, payload: {reportId, tagName}},
          {type: FETCH_ENCLAVE_TAGS}
        ]))
        .catch(err => ({
          type: REMOVE_ENCLAVE_TAG_FAILURE,
          meta: {
            alert: {type: 'error', text: 'Could not remove enclave tag from report'},
          }
        }));
    });

const removeAttributeEpic = (action$, store, {ajax}) =>
  action$.ofType(REMOVE_ATTRIBUTE)
    .mergeMap(({payload: {reportId, tagName}}) => {
      return ajax.delete(`${REPORTS_ENDPOINT}/${reportId}/public-tags/${tagName}`)
        .mapTo({
          type: REMOVE_ATTRIBUTE_SUCCESS,
          payload: {reportId, tagName},
        })
        .catch(err => ({
          type: REMOVE_ATTRIBUTE_FAILURE,
          meta: {
            alert: {type: 'error', text: 'Could not remove attribute from report'},
          },
        }));
    });

const removeReportEpic = (action$, store, {ajax}) =>
  action$.ofType(REMOVE)
    .mergeMap(({payload: id}) => {
      return ajax.delete(REPORTS_ENDPOINT + '/' + id)
        .mergeMap(() => ([
          {type: REMOVE_SUCCESS, payload: id},
          success('Successfully deleted report'),
        ]))
        .catch(err => ({
          type: REMOVE_FAILURE,
          error: true,
          payload: err,
          meta: {
            alert: {type: 'error', text: 'Could not delete report'},
          }
        }));
    });

/* Submission Service Additions to API */
const copyReportEpic = (action$, store, {ajax}) =>
  action$.ofType(COPY_REPORT)
    .map(prop('payload'))
    .mergeMap(({destEnclaveGuid, reportId, copyFromProvidedSubmission = false, reportJSON = null}) => {

      /* If this is a complex report, load the report data for pre-copy editing and send to the update page
          If reportJSON already exists then that means we have already done the editing and are now ready
          to send the edited payload to be copied
          TODO:SubmissionService Has report already been copied? Phase II
      */

      if (copyFromProvidedSubmission && !reportJSON) {
        const updateLink = `/update/${reportId}/enclave/${destEnclaveGuid}`;
        const loadReportDataAction = {
          type: LOAD_REPORT_DATA,
          payload: {guid: reportId}
        };
        return Observable.from([
          loadReportDataAction,
          replace(updateLink),
        ]);
      }

      const fullReportMap = copyFromProvidedSubmission ?
        reportJSON || currentReportOrListItem(store.getState()) : null;
      const copyPayload = copyFromProvidedSubmission && fullReportMap ? toJSON(fullReportMap) : null;
      const queryParams = `destEnclaveGuid=${destEnclaveGuid}&copyFromProvidedSubmission=${copyFromProvidedSubmission.toString()}`;


      return ajax.postJSON(`${REPORTS_ENDPOINT}/copy/${reportId}?${queryParams}`, copyPayload)
        .map(prop('response'))
        .mergeMap(response => {
          const enclavesStore = enclavesMap(store.getState());
          const destinationEnclaveName = enclavesStore.get(destEnclaveGuid).get('name');
          const copiedReportLink = `/constellation/reports/${response.id}`;

          // TODO:SUBMISSION REFACTOR ASAP
          const successMessageTemplate =
            {
              type: ADD_ALERT,
              payload: {type: 'success', text: 'boo', htmlMessage: <span> New Copy <a href={copiedReportLink}> {response.id} </a> created in Enclave: {destinationEnclaveName}</span>},
            };

          const postOpActions = [{
            type: COPY_REPORT_SUCCESS,
            payload: response,
          }, successMessageTemplate];

          if (reportJSON) {
            const originalReportRoute = `/constellation/reports/${reportId}`;
            postOpActions.push(replace(originalReportRoute));
          }

          return postOpActions;
        })
        .catch(err => {
          console.error(err);
          const errorMessage = err.status === 403 ? 'You do not have the required permissions to copy to this enclave' : 'Could not copy report, if this problem persists contact support';
          return Observable.of({
            type: COPY_REPORT_FAILURE,
            meta: {
              alert: {type: 'error', text: errorMessage},
            }
          })});
    })
    .catch(err => {
      console.error(err);
      return Observable.of({
        type: COPY_REPORT_FAILURE,
        meta: {
          alert: {type: 'error', text: 'Could not copy report, if this problem persists contact support'},
        }
      })});


const moveReportEpic = (action$, store, {ajax}) =>
  action$.ofType(MOVE_REPORT)
    .map(prop('payload'))
    .mergeMap(({destEnclaveGuid, reportId}) => {

      const queryParams = `destEnclaveGuid=${destEnclaveGuid}`;
      return ajax.postJSON(`${REPORTS_ENDPOINT}/move/${reportId}?${queryParams}`)
        .map(prop('response'))
        .mergeMap(response => {
          return [{
            type: MOVE_REPORT_SUCCESS,
            payload: response,
          }, success(`Report moved to enclave ${destEnclaveGuid} successfully`)];
        })
        .catch(err => {
          const errorMessage = err.status === 403 ? 'You do not have the required permissions to move to this enclave' : 'Could not move report, if this problem persists contact support';
          return Observable.of({
            type: MOVE_REPORT_FAILURE,
            meta: {
              alert: {type: 'error', text: errorMessage},
            }
          })});
    })
    .catch(err => {
      return Observable.of({
        type: MOVE_REPORT_FAILURE,
        meta: {
          alert: {type: 'error', text: 'Could not move report, if this problem persists contact support'},
        }
      })});

/**
 * If users set their browsers to block any files from downloading then the following functions
 * can still be succeffully invoked but no files will be downloaded
 *
 * NOTE: Not yet tested directly without axios, but manually issuing a request
 * worked fine in the browser. Example:
 * ajax.get('http://localhost:9090/services/incident-reports/970dd7d8-507d-4975-837f-6082e1db0aa8/download.json')
 **/
const exportReportsEpic = (action$, store, {ajax}) =>
  action$.ofType(EXPORT_REPORT)
    .mergeMap(({payload: {id, format}}) => {
      return ajax.get(`${REPORTS_ENDPOINT}/${id}/download.${format}`, {}, {responseType: 'text'})
        .do(({response}) => {
          const filename = `report-${id}.${format === 'fire_eye' ? 'csv' : format}`;
          saveTextToFile(filename, response);
        })
        .ignoreElements()
        .catch(err => {
          return Observable.of(
            error(`Could not export report in ${format} format.`)
          );
        });
    });

const reindexReportEpic = (action$, store, {ajax}) =>
  action$.ofType(REINDEX)
    .do(() => store.dispatch(warn('Reindexing report...')))
    .mergeMap(({payload: id}) => {
      return ajax.get('/services/incident-reports/reprocess/' + id)
        .mergeMap(response => {
          const nodes = Constellation.getters.currentGraph(store.getState())
            .getIn(['data', 'nodes']).toList().map(x => x.get('id'));
          return fetchConstellationById(id, ajax)
            .mergeMap(({response}) => {
              const newNodes = fromJS(response).get('nodes').map(x => x.get('id'));
              const nodesToRemove = nodes.filter(x => !newNodes.includes(x));
              return Observable.from([
                {type: REINDEX_SUCCESS, payload: id},
                success('Successfully reindexed report'),
                /* Unclear if all of this is necessary: */
                Constellation.actions.replaceNodeSelection(nodesToRemove),
                Constellation.actions.deleteCurrentSelection(),
                Constellation.actions.replaceNodeSelection([id]),
              ]).delay(3000);
            });
        })
        .catch(err => {
          return Observable.from([
            {type: REINDEX_FAILURE},
            error('Could not reindex report'),
          ]);
        });
    }).catch(handleError);

export default combineEpics(
  setFetchOptionsEpic,
  copyReportEpic,
  fetchMetadataEpic,
  setEnclaveTagsBulk,
  pollReportAndStore,
  fetchAndStoreEpic,
  initializeEpic,
  moveReportEpic,
  attributeEpic,
  removeEnclaveTagEpic,
  removeAttributeEpic,
  removeReportEpic,
  exportReportsEpic,
  reindexReportEpic,
);



// WEBPACK FOOTER //
// ./src/app/modules/reports/epics.js